import React, {createContext, useContext, useState} from "react"
import {Button, ButtonGroup, Link, LoadingButton, Modal, ModalBody, ModalFooter, ModalHeader, Typography, TextField} from "@one-thd/sui-atomic-components";
import thdCustomer from "@thd-olt-global/thd-customer";
import { unenrollFromPasswordless } from "../../../../api/custAccountApi";
import { useCustomerAccount } from "../../AccountProvider";
import { useCommonContext } from "../../CommonProvider";
import { MESSAGE, MESSAGE_TYPE } from "../../../../constants/messages";
import analytics from "../../../../utils/analytics";
import { FEATURE_SWITCHES, isFeatureSwitchEnabled, isNativeApp, postDataToEventBus, isSetPasswordInSigninWithPasswordInstead, formatPhoneNumber2 } from "../../../../utils";
import { setWebauthnNoThanksWeb } from "../../../../utils/webauthnUtils";
import {getFirstPasswordError, getPasswordStrengthScore} from "../../../../utils/validators";
import { CSSTransition } from "react-transition-group";
import PasswordRequirementsOutline from "../../../../components/common/PasswordRequirementOutline/PasswordRequirementsOutline";
import { getOtpOptions, getOTPv2 } from "../../../../api/custAccountApi";
import { updatePasswordWithOTP } from "../../../../api/custAccountApi";
import { OtpLoadingView } from "../../../../components/NewVerifyOtpModal/OtpModalViews/";
import { getErrorCode, handleApiError } from "../../../../utils/errors";
import { setUrl } from "../../../../utils/urls";
import { API_URLS } from "../../../../constants/urls";
import {PasswordModalContext, usePasswordModalContext} from "./PasswordModalContext";


export const unenrollmentSubmitButtonAutomationId = "passwordless-unenrollment--submit-button";
export const unenrollmentCancelButtonAutomationId = "passwordless-unenrollment--cancel-button";
export const unenrollmentLinkAutomationId = "passwordless-unenrollment--link";

const START_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD_VIEW = "START_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD_VIEW";
const START_VERIFY_IDENTITY_VIEW = "START_VERIFY_IDENTITY_VIEW";
const START_VERIFY_CODE_VIEW = "START_VERIFY_CODE_VIEW";

const RESEND_OTP_CODE_OPTIONS = {
  email: [
    { text: "Email me a new code", value: "email"},
    { text: "Text me a code", value: "sms"},
    { text: "Call me with a code", value: "phone"}
  ],
  sms: [
    { text: "Text me a new code", value: "sms"},
    { text: "Email me a code", value: "email"},
    { text: "Call me with a code", value: "phone"}
  ],
  phone: [
    { text: "Call me with a new code", value: "phone"},
    { text: "Email me a code", value: "email"},
    { text: "Text me a code", value: "sms"}
  ]
}

function PasswordlessUnenrollmentModal({onUnenroll, isSubmitting}) {

  const {isOpen, onClose} = usePasswordModalContext()

  return (
      <Modal open={isOpen} onClose={onClose} size='sm'>
        <ModalHeader>
          Sign In Options
        </ModalHeader>

        <ModalBody>
          <Typography weight="regular" variant="body-base">
            Are you sure you want to sign in with a password?
          </Typography>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup orientation="vertical">
            <LoadingButton type="submit" loading={isSubmitting} variant="primary" fullWidth onClick={onUnenroll} data-automation-id={unenrollmentSubmitButtonAutomationId}>
              Yes, continue
            </LoadingButton>
            <Button variant="secondary" fullWidth onClick={onClose} data-automation-id={unenrollmentCancelButtonAutomationId}>
              No, cancel
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
  )
}

function SetPasswordInSigninWithPasswordInstead({ setStartVerifyIdentityView }) {

  const { isOpen, onClose, email, password, setPassword } = usePasswordModalContext()

  const [showPass, setShowPass] = useState(false);
  const [isNewPasswordFocus, setIsNewPasswordFocus] = useState(false);

  const errorMessage = getFirstPasswordError(email, password);
  const status = (password == null || password == "")
    ? ""
    : (errorMessage == null ? "success" : "error")
  const statusMessage = (status == "error" ? errorMessage : "")
  const isValid = status == "success" && getPasswordStrengthScore(password, email) >= 4

  console.log({
    errorMessage,
    status,
    statusMessage,
    email,
    password,
    isValid,
    strength: getPasswordStrengthScore(password, email)
  })

  return (
    <Modal open={isOpen} onClose={onClose} size='md'>
        <ModalHeader>
          Create your password
        </ModalHeader>

        <ModalBody>
          <Typography weight="regular" variant="body-base">
            Create a strong password.
          </Typography>

          <form className="sui-flex sui-gap-2">
            <div className="sui-w-4/5">
              <TextField
                id="set-password"
                label="Password"
                value={password.value}
                fullWidth
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setIsNewPasswordFocus(true)}
                status={status}
                statusMessage={statusMessage}
                type={showPass ? "text" : "password"}
              />
            </div>
            <div className="sui-w-1/5 sui-m-auto">
              <Button variant="text" onClick={() => setShowPass(!showPass)}>
                {showPass ? "Hide":"Show"}
              </Button>
            </div>
          </form>

          <CSSTransition
            in={ isNewPasswordFocus  }
            timeout={0}
            classNames="error"
            unmountOnExit
          >

            <PasswordRequirementsOutline
              email={email}
              password={password}
            />
          </CSSTransition>

        </ModalBody>

        <ModalFooter>
          <ButtonGroup orientation="vertical">
            <Button type="button"  variant="primary" fullWidth disabled={!isValid} onClick={setStartVerifyIdentityView} data-automation-id={unenrollmentSubmitButtonAutomationId}>
              Save Password
            </Button>
            <Button variant="secondary" fullWidth onClick={onClose} >
              Cancel
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
  )
}

function VerifyIdentity({setVerifyCodeView}) {

  const {isOpen, onClose, email} = usePasswordModalContext()

  return (
    <Modal open={isOpen} onClose={onClose} size='md'>
      <ModalHeader>
        Verify Your Identity
      </ModalHeader>

      <ModalBody>
        <Typography weight="regular" variant="body-base">
          Before we save this password, we need to confirm your identity by sending a one-time verification code to your email address:
        </Typography>

        <div className="sui-my-4">
          <Typography weight="bold" variant="body-base">
            {email}
          </Typography>
        </div>

      </ModalBody>

      <ModalFooter>
        <ButtonGroup orientation="vertical">
          <Button variant="primary" type="button" fullWidth onClick={setVerifyCodeView}>
            Send Code
          </Button>
          <Button variant="secondary" fullWidth onClick={onClose} data-automation-id={unenrollmentCancelButtonAutomationId}>
            No, cancel
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  )
}

function VerifyCode({verificationOption, setVerificationOption, existingPhoneNumber, otpOptions, setEncryptedPayload, encryptedPayload,  setIsSubmitting, isSubmitting, unenrollFromPasswordlessAPI, errorToShow, setErrorToShow}) {

  const { isOpen, onClose, email, password } = usePasswordModalContext()

  const [ OTP, setOTP] = useState("");

  const onChangeOTP = (e) => {
    const otp = e.target.value.trim().replace(/[^\d]/, "");
    setOTP(otp);
  };

  const otpOption = (verificationOption === "email") ? email : formatPhoneNumber2(existingPhoneNumber);
  const showPhoneOptions = (otpOptions.length > 1);
  const enableSubmitButton = OTP.length === 6;
  const showErrorMessage = errorToShow !== ""


  const resendCode = (otpChannel) => {
    setIsSubmitting(true);
    getOTPv2(email, encryptedPayload, otpChannel).then(({response, error}) => {
      setIsSubmitting(false);
      if (response) {
        setEncryptedPayload(response.data?.encryptedPayload);
      } else {
        const errorMessage = handleApiError(error?.response?.data?.errorMessage, "update password with otp in sign in with password");
        setErrorToShow(errorMessage)
      }
    });
    setVerificationOption(otpChannel);
  }

  const submitUpdatePassword = () => {

    setIsSubmitting(true);
    updatePasswordWithOTP(password, email, encryptedPayload, OTP, "UnenrollmentPasswordSet").then(({response, error}) => {
      setIsSubmitting(false);
      if (response) {
        unenrollFromPasswordlessAPI();
        onClose();

      } else {
        const errorCode = getErrorCode(error?.response?.data?.errorCode);
        const errorMessage = handleApiError(error?.response?.data?.errorMessage, "update password with otp in sign in with password");
        if (errorCode === "AUTH_ERR_125") {
          setErrorToShow(MESSAGE.OTP_ATTEMPTS_EXHAUSTED)
        } else {
          setErrorToShow(errorMessage)

        }
      }
    });
  }

  return (
    <Modal open={isOpen} onClose={onClose} size='md'>
      <ModalHeader>
        Verify Your Identity
      </ModalHeader>

      <ModalBody>
        {isSubmitting
          ? <OtpLoadingView />
          : (
            <div className='sui-grid sui-gap-4'>
              <Typography weight="regular" variant="body-base">
                We sent a verification code to <strong>{otpOption}</strong>
              </Typography>

              <TextField
                id="set-password"
                label="Enter Verification Code"
                value={OTP || ""}
                onChange={onChangeOTP}
                type="tel"
                fullWidth
                InputProps={{ inputAttributes: { maxLength: "6"}}}
                status={showErrorMessage && "error"}
                statusMessage={showErrorMessage && errorToShow}
              />

              <Typography weight="regular" variant="body-base">
                Having issues?
              </Typography>

              <Typography weight="regular" variant="body-base">
                Here are some options.
              </Typography>

            <div className="sui-my-4 sui-flex sui-flex-col sui-items-baseline sui-gap-2">
              <button type="button" onClick={() => resendCode(RESEND_OTP_CODE_OPTIONS[verificationOption][0].value)} className="sui-font-regular sui-text-base sui-tracking-normal sui-normal-case sui-line-clamp-unset sui-text-primary sui-underline" >
                {RESEND_OTP_CODE_OPTIONS[verificationOption][0].text}
              </button>
              {showPhoneOptions &&
              <>
                <button type="button" onClick={() => resendCode(RESEND_OTP_CODE_OPTIONS[verificationOption][1].value)} className="sui-font-regular sui-text-base sui-tracking-normal sui-normal-case sui-line-clamp-unset sui-text-primary sui-underline" >
                  {RESEND_OTP_CODE_OPTIONS[verificationOption][1].text}
                </button>
                <button type="button" onClick={() => resendCode(RESEND_OTP_CODE_OPTIONS[verificationOption][2].value)} className="sui-font-regular sui-text-base sui-tracking-normal sui-normal-case sui-line-clamp-unset sui-text-primary sui-underline" >
                  {RESEND_OTP_CODE_OPTIONS[verificationOption][2].text}
                </button>
              </>
              }
            </div>
          </div>
          )}
      </ModalBody>

      <ModalFooter>
        <ButtonGroup orientation="vertical">
          <Button variant="primary" type="submit"  onClick={() => submitUpdatePassword()} fullWidth data-automation-id={unenrollmentSubmitButtonAutomationId} disabled={!enableSubmitButton}>
            Submit
          </Button>
          <Button variant="secondary" fullWidth onClick={onClose} data-automation-id={unenrollmentCancelButtonAutomationId}>
            No, cancel
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  )
}

export const PasswordlessUnenrollment = () => {

  const { setStale, account, email } = useCustomerAccount();
  const { addAlert } = useCommonContext();
  const { isNoPasswordSet = "", profile } = account;
  const { existingPhoneNumber = ""  } = profile ;

  const [ isOpen, setIsOpen ] = useState(false);
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ password, setPassword] = useState("");
  const [ unenrollModalState, setUnenrollModalState ] = useState("")
  const [ verificationOption, setVerificationOption ] = useState("");
  const [ encryptedPayload, setEncryptedPayload ] = useState();
  const [ otpOptions, setOtpOptions] = useState()
  const [ errorToShow, setErrorToShow ] = useState("")



  const openModal = () => {
    setIsOpen(true);
    analytics.postOverlayEvent("unenroll from siwp");
  }
  const onClose = () => {
    if(isSetPasswordInSigninWithPasswordInstead()){
      setIsSubmitting(false);
      setUnenrollModalState("");
      setPassword("");
    }
    setIsOpen(false);
  };


  const setVerifyCodeView = () => {
    setUnenrollModalState(START_VERIFY_CODE_VIEW)
    getOTPv2(email).then(({response, error})=>{
      if(response?.data?.encryptedPayload) {
        // Successfully received encrypted payload from GET OTP request.
        setEncryptedPayload(response.data?.encryptedPayload)
      }else if (error) {
        setErrorToShow(error?.response?.data?.errorMessage)
      }
    })
  }

  const setStartVerifyIdentityView = () => {
    setUnenrollModalState(START_VERIFY_IDENTITY_VIEW);
    setVerificationOption("email");

    getOtpOptions(email).then(({response, error})=>{
      if(response?.data?.otpOptions){
        setOtpOptions(response?.data?.otpOptions)
      }else{
        setErrorToShow(error?.response?.data?.errorMessage)
      }
    })
  }

  const unenrollFromPasswordlessAPI = () => {
    if(!thdCustomer?.isLoggedIn || thdCustomer?.isPersisted) {
      setUrl("", API_URLS.SIGNIN);
      return
    }
    unenrollFromPasswordless().then(({ response, error }) => {
      setIsSubmitting(false);
      onClose();
      if (response) {
        if(isSetPasswordInSigninWithPasswordInstead()){
          addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.SET_PASSWORD_SIGNIN_WITH_PASSWORD_INSTEAD_SUCCESS);
        }else{
          addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.PASSWORDLESS_UNENROLL_SUCCESS);
        }
        if (isNativeApp()) {
          postDataToEventBus("NATIVE_APP.PASSWORDLESS_UNENROLLMENT", { status: "successful" });
        }
        analytics.postSettingsSubmissionEvent("security: unenrolled from siwp");
        setStale();
      } else if (error) {
        addAlert(MESSAGE_TYPE.DANGER, MESSAGE.SOMETHING_WENT_WRONG);
      }
    });
  }

  const onUnenroll = () => {
    setIsSubmitting(true);

    if(isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_WEB_AUTHN_UNENROLL)){
      setWebauthnNoThanksWeb(email);
    }

    if(isSetPasswordInSigninWithPasswordInstead() && isNoPasswordSet === true){
      setUnenrollModalState(START_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD_VIEW);
      setIsSubmitting(false);
    }else{
      unenrollFromPasswordlessAPI();
    }

  }

  const renderView = () => {
    switch(unenrollModalState){
      case START_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD_VIEW:
        return <SetPasswordInSigninWithPasswordInstead setStartVerifyIdentityView={setStartVerifyIdentityView} password={password} setPassword={setPassword}/>
      case START_VERIFY_IDENTITY_VIEW:
        return <VerifyIdentity setVerifyCodeView={setVerifyCodeView} />
      case START_VERIFY_CODE_VIEW:
        return <VerifyCode  email={email} verificationOption={verificationOption} setVerificationOption={setVerificationOption} existingPhoneNumber={existingPhoneNumber} otpOptions={otpOptions} setEncryptedPayload={setEncryptedPayload} encryptedPayload={encryptedPayload} setIsSubmitting={setIsSubmitting} isSubmitting={isSubmitting}  password={password} unenrollFromPasswordlessAPI={unenrollFromPasswordlessAPI} addAlert={addAlert} setErrorToShow={setErrorToShow} errorToShow={errorToShow} />
      default:
        return <PasswordlessUnenrollmentModal onUnenroll={onUnenroll}  isSubmitting={isSubmitting}/>
    }
  }

  return (
    <div className="col__12-12 u--paddingNone u--paddingXsmall-top">
      <button type="button"
        onClick={openModal}
        data-automation-id={unenrollmentLinkAutomationId}>
        <Link className="security-page-default-link">Sign in with a password instead</Link>
      </button>
      <PasswordModalContext.Provider value={{
        isOpen,
        onClose,
        email,
        password, setPassword
      }}>
        { renderView()}
      </PasswordModalContext.Provider>
    </div>
  );
}